// Map filter state — shared across cluster, quadrant, and cloud lenses.
//
// State of the world before this file: each viz function rendered the full
// graph. To get filtering, we now subset (categories, trends, topics,
// topicTrends) BEFORE handing them to the renderer. The renderers themselves
// don't need to know filters exist.
//
// Persistence: filters survive a tab switch via location.hash (so cockpit
// drill-ins like #/cluster?macro=tech&lens=client work) AND survive a reload
// via localStorage. The hash takes precedence on load.
//
// Saved views: a named filter snapshot, persisted in localStorage. Backend
// table comes later when we have multi-user views.

import { LENSES, lensOfBusinessLine, MACROS, macroOfCategory, type Lens, type Macro } from "./macros.ts";
import type { BusinessLine, Category, Topic, TopicTrend, Trend, TopicWithContext } from "./types.ts";

export interface MapFilters {
  search: string;
  /** Selected outside macros. Empty = all. */
  macros: Set<Macro>;
  /** Selected inside lenses. Empty = all. */
  lenses: Set<Lens>;
  /** Only topics flagged h3_thesis_flag. */
  h3Only: boolean;
  /** Only topics that moved during the active period (proxy: ai_drafted=false). */
  movedOnly: boolean;
}

export const EMPTY_FILTERS: MapFilters = {
  search: "",
  macros: new Set(),
  lenses: new Set(),
  h3Only: false,
  movedOnly: false,
};

const LS_KEY = "tt:filters:v1";
const LS_VIEWS_KEY = "tt:saved-views:v1";

export function cloneFilters(f: MapFilters): MapFilters {
  return {
    search: f.search,
    macros: new Set(f.macros),
    lenses: new Set(f.lenses),
    h3Only: f.h3Only,
    movedOnly: f.movedOnly,
  };
}

export function isEmpty(f: MapFilters): boolean {
  return !f.search && f.macros.size === 0 && f.lenses.size === 0 && !f.h3Only && !f.movedOnly;
}

// ---------- Persistence ----------
export function loadFromHash(): Partial<MapFilters> {
  const q = location.hash.split("?")[1];
  if (!q) return {};
  const params = new URLSearchParams(q);
  const partial: Partial<MapFilters> = {};
  if (params.get("search")) partial.search = params.get("search")!;
  if (params.get("macro")) {
    partial.macros = new Set(
      params.get("macro")!.split(",").filter((m) => MACROS.includes(m as Macro)) as Macro[],
    );
  }
  if (params.get("lens")) {
    partial.lenses = new Set(
      params.get("lens")!.split(",").filter((l) => LENSES.includes(l as Lens)) as Lens[],
    );
  }
  if (params.get("h3") === "1") partial.h3Only = true;
  if (params.get("moved") === "1") partial.movedOnly = true;
  return partial;
}

export function loadFromStorage(): Partial<MapFilters> {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return {};
    const obj = JSON.parse(raw);
    return {
      search: obj.search || "",
      macros: new Set(obj.macros ?? []),
      lenses: new Set(obj.lenses ?? []),
      h3Only: !!obj.h3Only,
      movedOnly: !!obj.movedOnly,
    };
  } catch {
    return {};
  }
}

export function persist(f: MapFilters): void {
  try {
    localStorage.setItem(
      LS_KEY,
      JSON.stringify({
        search: f.search,
        macros: [...f.macros],
        lenses: [...f.lenses],
        h3Only: f.h3Only,
        movedOnly: f.movedOnly,
      }),
    );
  } catch {
    // localStorage may be disabled in some Safari modes; non-fatal.
  }
}

export function loadInitial(): MapFilters {
  return {
    ...EMPTY_FILTERS,
    ...loadFromStorage(),
    ...loadFromHash(),
  } as MapFilters;
}

// ---------- Subsetting ----------
export interface GraphSlice {
  categories: Category[];
  trends: Trend[];
  topics: Topic[];
  topicTrends: TopicTrend[];
}

/**
 * Apply filters to a full graph slice + per-topic context. Returns a new
 * slice scoped to the matching topics, plus categories/trends pruned to only
 * those that still have any matching topic. Order is preserved.
 */
export function applyFilters(
  full: GraphSlice,
  ctxs: TopicWithContext[],
  businessLines: BusinessLine[],
  filters: MapFilters,
): { slice: GraphSlice; ctxs: TopicWithContext[]; matchedCount: number } {
  if (isEmpty(filters)) {
    return { slice: full, ctxs, matchedCount: ctxs.length };
  }

  const macroByCat = new Map<number, Macro>();
  for (const c of full.categories) macroByCat.set(c.id, macroOfCategory(c));
  const lensByBl = new Map<number, Lens>();
  for (const bl of businessLines) lensByBl.set(bl.id, lensOfBusinessLine(bl));

  const search = filters.search.trim().toLowerCase();

  const matchedTopicIds = new Set<number>();
  const matchedCtxs: TopicWithContext[] = [];

  for (const ctx of ctxs) {
    const t = ctx.topic;

    // Search match — name, definition, search_terms, aliases.
    if (search) {
      const haystacks = [t.name, t.definition ?? "", ...(t.search_terms ?? []), ...(t.aliases ?? [])]
        .join(" ").toLowerCase();
      if (!haystacks.includes(search)) continue;
    }

    // Macro filter — topic must touch at least one selected macro.
    if (filters.macros.size > 0) {
      const topicMacros = new Set<Macro>();
      for (const m of ctx.memberships) {
        const mc = macroByCat.get(m.category.id);
        if (mc) topicMacros.add(mc);
      }
      let any = false;
      for (const m of filters.macros) if (topicMacros.has(m)) { any = true; break; }
      if (!any) continue;
    }

    // Lens filter — topic must have ANY non-null relevance in a selected lens.
    if (filters.lenses.size > 0) {
      let any = false;
      for (const bl of businessLines) {
        const lens = lensByBl.get(bl.id);
        if (!lens || !filters.lenses.has(lens)) continue;
        if (ctx.relevance[bl.key]?.score != null) { any = true; break; }
      }
      if (!any) continue;
    }

    if (filters.h3Only && !t.h3_thesis_flag) continue;
    if (filters.movedOnly && t.ai_drafted) continue;

    matchedTopicIds.add(t.id);
    matchedCtxs.push(ctx);
  }

  const matchedTopics = full.topics.filter((t) => matchedTopicIds.has(t.id));
  const matchedTT = full.topicTrends.filter((tt) => matchedTopicIds.has(tt.topic_id));
  const liveTrendIds = new Set(matchedTT.map((tt) => tt.trend_id));
  const matchedTrends = full.trends.filter((tr) => liveTrendIds.has(tr.id));
  const liveCatIds = new Set(matchedTrends.map((tr) => tr.category_id));
  const matchedCats = full.categories.filter((c) => liveCatIds.has(c.id));

  return {
    slice: {
      categories: matchedCats,
      trends: matchedTrends,
      topics: matchedTopics,
      topicTrends: matchedTT,
    },
    ctxs: matchedCtxs,
    matchedCount: matchedCtxs.length,
  };
}

// ---------- Saved views ----------
export interface SavedView {
  id: string;
  name: string;
  filters: MapFilters;
  /** ISO timestamp the view was last saved. */
  updated_at: string;
}

interface SavedViewWire {
  id: string;
  name: string;
  filters: {
    search: string;
    macros: Macro[];
    lenses: Lens[];
    h3Only: boolean;
    movedOnly: boolean;
  };
  updated_at: string;
}

function viewToWire(v: SavedView): SavedViewWire {
  return {
    id: v.id,
    name: v.name,
    updated_at: v.updated_at,
    filters: {
      search: v.filters.search,
      macros: [...v.filters.macros],
      lenses: [...v.filters.lenses],
      h3Only: v.filters.h3Only,
      movedOnly: v.filters.movedOnly,
    },
  };
}
function wireToView(w: SavedViewWire): SavedView {
  return {
    id: w.id,
    name: w.name,
    updated_at: w.updated_at,
    filters: {
      search: w.filters.search ?? "",
      macros: new Set(w.filters.macros ?? []),
      lenses: new Set(w.filters.lenses ?? []),
      h3Only: !!w.filters.h3Only,
      movedOnly: !!w.filters.movedOnly,
    },
  };
}

export function listSavedViews(): SavedView[] {
  try {
    const raw = localStorage.getItem(LS_VIEWS_KEY);
    if (!raw) return [];
    return (JSON.parse(raw) as SavedViewWire[]).map(wireToView);
  } catch {
    return [];
  }
}

export function saveView(name: string, filters: MapFilters): SavedView {
  const id = crypto.randomUUID?.() ?? `v-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const now = new Date().toISOString();
  const v: SavedView = { id, name, filters: cloneFilters(filters), updated_at: now };
  const list = listSavedViews().concat(v);
  localStorage.setItem(LS_VIEWS_KEY, JSON.stringify(list.map(viewToWire)));
  return v;
}

export function deleteView(id: string): void {
  const list = listSavedViews().filter((v) => v.id !== id);
  localStorage.setItem(LS_VIEWS_KEY, JSON.stringify(list.map(viewToWire)));
}

// ---------- Hash sync ----------
export function filtersToHashSuffix(f: MapFilters): string {
  if (isEmpty(f)) return "";
  const params = new URLSearchParams();
  if (f.search) params.set("search", f.search);
  if (f.macros.size) params.set("macro", [...f.macros].join(","));
  if (f.lenses.size) params.set("lens", [...f.lenses].join(","));
  if (f.h3Only) params.set("h3", "1");
  if (f.movedOnly) params.set("moved", "1");
  return `?${params.toString()}`;
}
